import React, { useEffect } from 'react';
import { X } from 'lucide-react';

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  headerRight,
  headerContent,
  footer,
  children,
  width = 'w-[560px]',
}) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex animate-fade-in">
      {/* Backdrop */}
      <div
        className="flex-1 bg-slate-900/30 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Panel */}
      <div className={`${width} max-w-[95vw] bg-slate-50 border-l border-slate-200 shadow-2xl flex flex-col animate-slide-in`}>
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-200 bg-white flex items-center gap-3">
          <div className="flex-1 min-w-0">
            {headerContent ?? (
              <>
                {subtitle && <div className="text-[11px] text-slate-400 font-mono">{subtitle}</div>}
                {title && <div className="text-[14px] font-semibold text-slate-900">{title}</div>}
              </>
            )}
          </div>
          {!headerContent && headerRight}
          <button
            onClick={onClose}
            className="w-8 h-8 grid place-items-center text-slate-500 hover:bg-slate-100 rounded-md flex-shrink-0"
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-5 py-3 border-t border-slate-200 bg-white flex gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function DrawerDetailGrid({ fields = [] }) {
  return (
    <div className="grid grid-cols-2 gap-4 bg-white border border-slate-200 rounded-xl p-4">
      {fields.map(({ label, value, mono }) => (
        <div key={label}>
          <div className="text-[11px] uppercase font-medium text-slate-400 mb-1">{label}</div>
          <div className={`font-semibold text-slate-900 ${mono ? 'font-mono text-[12px]' : ''}`}>{value}</div>
        </div>
      ))}
    </div>
  );
}

export function DrawerProgressBar({ percent, leftLabel, rightLabel }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[12.5px] font-semibold text-slate-900 mb-2">Progression</div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-teal-600 rounded-full" style={{ width: `${percent}%` }} />
      </div>
      <div className="flex justify-between text-[11.5px] text-slate-500 mt-1.5">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
    </div>
  );
}

export default Drawer;
